/** 週次レビュー（AI）。生成はアプリを開いたときに行い、通信はBFF経由でGeminiへ送る。 */

import { getDb } from '../db/schema';
import { enqueue } from '../db/outbox';
import { bumpData } from '../db/events';
import { listByRange } from '../db/entries';
import { listDailiesByRange } from '../db/dailies';
import { getCategoryMap, resolveCategory } from '../db/categories';
import { getGoals, activeAreas } from '../db/goals';
import {
  addDays,
  formatTime,
  startOfWeek,
  toDateKey,
  toWeekKey,
  weekKeyToStartDate,
} from '../lib/datetime';
import {
  cellKey,
  HORIZON_LABELS,
  HORIZONS,
  SCHEMA_VERSION,
  type WeeklyReview,
} from '../domain/types';

const AUTO_KEY = 'nj.weeklyReviewAuto';
const WEEKDAY = ['日', '月', '火', '水', '木', '金', '土'];

export function isAutoEnabled(): boolean {
  return localStorage.getItem(AUTO_KEY) === '1';
}

export function setAutoEnabled(on: boolean): void {
  localStorage.setItem(AUTO_KEY, on ? '1' : '0');
}

export async function getReview(week: string): Promise<WeeklyReview | undefined> {
  const db = await getDb();
  return db.get('reviews', week);
}

export async function listReviews(): Promise<WeeklyReview[]> {
  const db = await getDb();
  return (await db.getAll('reviews')).sort((a, b) => (a.week < b.week ? 1 : -1));
}

/** 直近で「終わっている」週。日曜の夜を境に、その週を対象とする。 */
export function lastCompletedWeek(now = new Date()): string {
  const thisWeekStart = startOfWeek(now);
  const isSundayEvening = now.getDay() === 0 && now.getHours() >= 21;
  // 月曜始まりなので、日曜21時以降は「今週」が実質終了しているとみなす
  return toWeekKey(isSundayEvening ? now : addDays(thisWeekStart, -1));
}

interface ReviewPayload {
  week: string;
  from: string;
  to: string;
  goals: { horizon: string; area: string; text: string }[];
  days: {
    date: string;
    weekday: string;
    daily: { events: string; good: string; action: string } | null;
    entries: { time: string; category: string; title: string; body: string }[];
  }[];
}

async function buildPayload(week: string): Promise<{ payload: ReviewPayload; count: number }> {
  const start = weekKeyToStartDate(week);
  const end = addDays(start, 6);
  const from = toDateKey(start);
  const to = toDateKey(end);

  const [entries, dailies, categories, goalsDoc] = await Promise.all([
    listByRange(from, to),
    listDailiesByRange(from, to),
    getCategoryMap(),
    getGoals(),
  ]);

  const goals = activeAreas(goalsDoc).flatMap((a) =>
    HORIZONS.map((h) => ({
      horizon: HORIZON_LABELS[h],
      area: a.name,
      text: goalsDoc.cells[cellKey(h, a.id)]?.text ?? '',
    })).filter((g) => g.text.trim() !== ''),
  );

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(start, i);
    const key = toDateKey(d);
    const daily = dailies.find((x) => x.date === key);
    return {
      date: key,
      weekday: WEEKDAY[d.getDay()],
      daily: daily ? { events: daily.events, good: daily.good, action: daily.action } : null,
      entries: entries
        .filter((e) => e.dateKey === key)
        .map((e) => ({
          time: formatTime(e.date),
          category: resolveCategory(categories, e.categoryId).name,
          title: e.title,
          body: e.body,
        })),
    };
  });

  return {
    payload: { week, from, to, goals, days },
    count: entries.length + dailies.length,
  };
}

export class NoDataError extends Error {
  constructor() {
    super('この週には記録がありません');
    this.name = 'NoDataError';
  }
}

/** 生成してローカルに保存し、OneDriveへの反映をキューに積む。 */
export async function generateReview(week: string): Promise<WeeklyReview> {
  const { payload, count } = await buildPayload(week);
  if (count === 0) throw new NoDataError();

  const res = await fetch('/api/review', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `レビューの生成に失敗しました (${res.status})`);
  }
  const out = (await res.json()) as {
    model: string;
    summary: string;
    aligned: string[];
    improve: string[];
    nextAction: string;
  };

  const review: WeeklyReview = {
    schemaVersion: SCHEMA_VERSION,
    week,
    from: payload.from,
    to: payload.to,
    generatedAt: new Date().toISOString(),
    model: out.model,
    summary: out.summary,
    aligned: out.aligned,
    improve: out.improve,
    nextAction: out.nextAction,
  };

  const db = await getDb();
  await db.put('reviews', review);
  await enqueue({ type: 'review.put', targetKey: `review:${week}`, week });
  bumpData();
  return review;
}

export async function deleteReview(week: string): Promise<void> {
  const db = await getDb();
  await db.delete('reviews', week);
  bumpData();
}

/**
 * アプリ起動時の自動生成。
 * 直近の終わった週のレビューが無ければ1回だけ生成する。
 */
export async function maybeGenerateOnOpen(): Promise<void> {
  if (!isAutoEnabled()) return;
  const week = lastCompletedWeek();
  if (await getReview(week)) return;
  try {
    await generateReview(week);
  } catch {
    // 記録なし・未設定・オフラインなどは黙って見送る。手動生成の導線は週ビューにある
  }
}
